import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { ExecutionEnvironmentDescriptor } from "./environment";
import { ServerAuthDescriptor } from "./auth";
import {
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas";
import { KeybindingCommand, KeybindingRule, ResolvedKeybindingsConfig } from "./keybindings";
import { EditorId, FileManagerRevealKind } from "./editor";
import { ModelCapabilities } from "./model";
import { ProviderKind } from "./orchestration";
import { ProviderCredentialScope, ProviderCredentialSource, ServerSettings } from "./settings";

const KeybindingsMalformedConfigIssue = Schema.Struct({
  kind: Schema.Literal("keybindings.malformed-config"),
  message: TrimmedNonEmptyString,
});

const KeybindingsInvalidEntryIssue = Schema.Struct({
  kind: Schema.Literal("keybindings.invalid-entry"),
  message: TrimmedNonEmptyString,
  index: Schema.Number,
});

export const ServerConfigIssue = Schema.Union([
  KeybindingsMalformedConfigIssue,
  KeybindingsInvalidEntryIssue,
]);
export type ServerConfigIssue = typeof ServerConfigIssue.Type;

const ServerConfigIssues = Schema.Array(ServerConfigIssue);

export const ServerProviderState = Schema.Literals(["ready", "warning", "error", "disabled"]);
export type ServerProviderState = typeof ServerProviderState.Type;

export const ServerProviderAuthStatus = Schema.Literals([
  "authenticated",
  "unauthenticated",
  "unknown",
]);
export type ServerProviderAuthStatus = typeof ServerProviderAuthStatus.Type;

export const ProviderRuntimeRole = Schema.Literals(["user-vm", "local"]);
export type ProviderRuntimeRole = typeof ProviderRuntimeRole.Type;

export const ProviderAuthProvenance = Schema.Struct({
  runtimeRole: ProviderRuntimeRole,
  credentialScope: ProviderCredentialScope,
  source: ProviderCredentialSource,
  homePath: Schema.optional(TrimmedNonEmptyString),
  binaryPath: Schema.optional(TrimmedNonEmptyString),
  accountLabel: Schema.optional(TrimmedNonEmptyString),
  injectedAt: Schema.optional(IsoDateTime),
  isHostInherited: Schema.Boolean,
});
export type ProviderAuthProvenance = typeof ProviderAuthProvenance.Type;

export const ServerProviderAuth = Schema.Struct({
  status: ServerProviderAuthStatus,
  type: Schema.optional(TrimmedNonEmptyString),
  label: Schema.optional(TrimmedNonEmptyString),
  accountEmail: Schema.optional(TrimmedNonEmptyString),
  provenance: Schema.optional(ProviderAuthProvenance),
});
export type ServerProviderAuth = typeof ServerProviderAuth.Type;

/**
 * Whether this Environment will let a person select the provider.
 *
 * Published for every provider the build knows about. A provider is never
 * dropped from the list: absence is not a state, and four different reasons
 * once collapsed into one "Coming soon." string because the Shell had to infer
 * meaning from what was missing. See
 * `docs/adr/withheld-providers-carry-their-reason.md`.
 */
export const ProviderAvailability = Schema.Literals(["available", "unavailable"]);
export type ProviderAvailability = typeof ProviderAvailability.Type;

/**
 * Why a Withheld Provider is withheld. The four members are the glossary's,
 * in `CONTEXT.md` section "Provider availability": Unbuilt, Not Offered, Not
 * Usable, Not on this Environment. Only Unbuilt may read "Coming soon."
 *
 * An Environment can only ever report Unbuilt, Not Usable, or Not on this
 * Environment. Not Offered is a CMMD fact about the organization's Provider
 * Offer, which the Shell adds when it joins.
 */
export const ProviderUnavailableReason = Schema.Literals([
  "unbuilt",
  "not-offered",
  "not-usable",
  "not-on-this-environment",
]);
export type ProviderUnavailableReason = typeof ProviderUnavailableReason.Type;

/**
 * The Not Usable sub-case. It picks a second line of copy, not a fifth state,
 * which is why it is a separate field rather than three more reasons.
 */
export const ProviderUnavailableDetail = Schema.Literals([
  "no-connection",
  "delivery-pending",
  "reconnect-required",
]);
export type ProviderUnavailableDetail = typeof ProviderUnavailableDetail.Type;

/**
 * Whether the check behind this snapshot's `installed`/`status`/`auth`/`models`
 * fields actually completed (`confirmed`), or a probe failed, timed out, or
 * errored before it could answer (`could-not-check`). Absent means the
 * snapshot predates this field and carries no opinion either way.
 *
 * Deliberately not a new `ProviderUnavailableReason` member, and not folded
 * into `ProviderUnavailableDetail`. That union is the wire vocabulary a
 * separate CMMD receiver already validates with a `.strict()` schema (see
 * `docs/adr/withheld-providers-carry-their-reason.md`), and a value that
 * receiver has never heard of fails the whole snapshot, which its outbox then
 * retries forever. A brand new field costs a receiver that has never heard of
 * it nothing: Effect Schema drops an unrecognized struct property instead of
 * failing the decode, and `serializeProviderCatalog`
 * (`apps/server/src/runtimeReplication/providerCatalog.ts`) never copies this
 * field to that wire in the first place, so the CMMD-facing shape does not
 * change at all.
 */
export const ProviderAvailabilityCheck = Schema.Literals(["confirmed", "could-not-check"]);
export type ProviderAvailabilityCheck = typeof ProviderAvailabilityCheck.Type;

/**
 * Whether the provider CLI honours the credential-directory variable, as this
 * Environment's probe found. `inconclusive` is a probe that ran and could not
 * tell. It is not `unsupported`, and it is never reported as `supported`. The
 * members are CMMD's `forgeRuntimeInventoryCredentialDirectoryAnswers`.
 */
export const ProviderCredentialDirectoryAnswer = Schema.Literals([
  "supported",
  "unsupported",
  "inconclusive",
]);
export type ProviderCredentialDirectoryAnswer = typeof ProviderCredentialDirectoryAnswer.Type;

/**
 * Which Credential Store the provider CLI uses on this Environment: its file,
 * the operating system's store, or `unknown` when it cannot be read the way
 * the CLI reads it. The members are CMMD's `forgeRuntimeInventoryCredentialStores`.
 */
export const ProviderCredentialStore = Schema.Literals(["file", "os", "unknown"]);
export type ProviderCredentialStore = typeof ProviderCredentialStore.Type;

export const ServerProviderModel = Schema.Struct({
  slug: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  isCustom: Schema.Boolean,
  isDefault: Schema.optional(Schema.Boolean),
  isLegacy: Schema.optional(Schema.Boolean),
  capabilities: Schema.NullOr(ModelCapabilities),
});
export type ServerProviderModel = typeof ServerProviderModel.Type;

export const ServerProviderSlashCommandInput = Schema.Struct({
  hint: TrimmedNonEmptyString,
});
export type ServerProviderSlashCommandInput = typeof ServerProviderSlashCommandInput.Type;

export const ServerProviderSlashCommand = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.optional(TrimmedNonEmptyString),
  input: Schema.optional(ServerProviderSlashCommandInput),
});
export type ServerProviderSlashCommand = typeof ServerProviderSlashCommand.Type;

export const ServerProviderSkill = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.optional(TrimmedNonEmptyString),
  path: TrimmedNonEmptyString,
  scope: Schema.optional(TrimmedNonEmptyString),
  enabled: Schema.Boolean,
  displayName: Schema.optional(TrimmedNonEmptyString),
  shortDescription: Schema.optional(TrimmedNonEmptyString),
});
export type ServerProviderSkill = typeof ServerProviderSkill.Type;

export const ServerProvider = Schema.Struct({
  provider: ProviderKind,
  enabled: Schema.Boolean,
  installed: Schema.Boolean,
  version: Schema.NullOr(TrimmedNonEmptyString),
  status: ServerProviderState,
  auth: ServerProviderAuth,
  checkedAt: IsoDateTime,
  message: Schema.optional(TrimmedNonEmptyString),
  models: Schema.Array(ServerProviderModel),
  /**
   * Optional on decode for one reason: during a rollout window the cloud Shell
   * decodes live snapshots from Environments still running the previous
   * release, which send no availability at all. A required field would fail
   * that decode and blank the picker for every such Environment. The server
   * always emits it.
   */
  availability: Schema.optional(ProviderAvailability),
  unavailableReason: Schema.optional(ProviderUnavailableReason),
  unavailableDetail: Schema.optional(ProviderUnavailableDetail),
  /**
   * Set by the provider Layer that produced this snapshot (`could-not-check`
   * for a probe that failed, timed out, or errored) and, when the registry
   * carries a previous good reading forward across such a probe, kept as
   * `could-not-check` to mark that carried-forward reading (forge#3588). See
   * `ProviderAvailabilityCheck`.
   */
  availabilityCheck: Schema.optional(ProviderAvailabilityCheck),
  /**
   * Set only when this snapshot's `installed`/`status`/`auth`/`models` fields
   * are carried forward from an earlier confirmed check rather than this
   * cycle's: the ISO time of that earlier confirmation. `checkedAt` still
   * advances every cycle, confirmed or not, so `lastConfirmedAt` is the one
   * field that answers "when did we last actually know this," and it does not
   * advance again until a fresh check confirms the reading anew.
   */
  lastConfirmedAt: Schema.optional(IsoDateTime),
  /**
   * Claude only, and absent until there is an answer. Neither has a default:
   * a runtime that has not answered yet must not read as one that has. See
   * `docs/adr/delivery-clears-the-os-credential-store-first.md`.
   */
  credentialDirectory: Schema.optional(ProviderCredentialDirectoryAnswer),
  credentialStore: Schema.optional(ProviderCredentialStore),
  slashCommands: Schema.Array(ServerProviderSlashCommand).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  skills: Schema.Array(ServerProviderSkill).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type ServerProvider = typeof ServerProvider.Type;

export const ServerProviders = Schema.Array(ServerProvider);
export type ServerProviders = typeof ServerProviders.Type;

export const ServerObservability = Schema.Struct({
  logsDirectoryPath: TrimmedNonEmptyString,
  localTracingEnabled: Schema.Boolean,
  otlpTracesUrl: Schema.optional(TrimmedNonEmptyString),
  otlpTracesEnabled: Schema.Boolean,
  otlpMetricsUrl: Schema.optional(TrimmedNonEmptyString),
  otlpMetricsEnabled: Schema.Boolean,
  otlpLogsUrl: Schema.optional(TrimmedNonEmptyString),
  otlpLogsEnabled: Schema.Boolean,
});
export type ServerObservability = typeof ServerObservability.Type;

export const ServerConfig = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
  auth: ServerAuthDescriptor,
  cwd: TrimmedNonEmptyString,
  keybindingsConfigPath: TrimmedNonEmptyString,
  keybindings: ResolvedKeybindingsConfig,
  issues: ServerConfigIssues,
  providers: ServerProviders,
  availableEditors: Schema.Array(EditorId),
  /** Whether file-manager launches can select a concrete target. */
  shellRevealInFileManager: Schema.optionalKey(Schema.Boolean),
  /** Runtime-appropriate wording for the reveal action. */
  shellRevealInFileManagerKind: Schema.optionalKey(FileManagerRevealKind),
  /**
   * Whether a thread subscription can emit the opt-in catch-up completion
   * marker. A client only asks for the marker when this is true, so it never
   * waits for a frame an older server will not send.
   */
  threadResumeCompletionMarker: Schema.optionalKey(Schema.Boolean),
  /**
   * Whether thread detail reads accept a turn window (`turnLimit` and
   * `beforeCursor`) and answer with `page` metadata. A client must not send
   * window fields to a server that does not advertise this: a windowed cache
   * held against a server with no cursor reads can never load its older turns.
   */
  threadSnapshotPagination: Schema.optionalKey(Schema.Boolean),
  /**
   * Whether this server serves `orchestration.getThreadDetailPage`, the RPC
   * "load earlier turns" reads over the socket on a connection with no HTTP
   * endpoint (a relay-only connection). A client must not send this method to
   * a server that does not advertise it.
   */
  threadDetailPageRpc: Schema.optionalKey(Schema.Boolean),
  observability: ServerObservability,
  settings: ServerSettings,
});
export type ServerConfig = typeof ServerConfig.Type;

export const ServerUpsertKeybindingInput = KeybindingRule;
export type ServerUpsertKeybindingInput = typeof ServerUpsertKeybindingInput.Type;

export const ServerUpsertKeybindingResult = Schema.Struct({
  keybindings: ResolvedKeybindingsConfig,
  issues: ServerConfigIssues,
});
export type ServerUpsertKeybindingResult = typeof ServerUpsertKeybindingResult.Type;

/**
 * Identifies a custom keybinding to drop.
 *
 * The command alone, because the store keys custom rules by command: an upsert
 * replaces every entry for a command, and a default is suppressed whenever any
 * custom rule claims its command. Accepting `key`/`when` here would describe a
 * removal the store cannot perform.
 */
export const ServerRemoveKeybindingInput = Schema.Struct({
  command: KeybindingCommand,
});
export type ServerRemoveKeybindingInput = typeof ServerRemoveKeybindingInput.Type;

export const ServerRemoveKeybindingResult = ServerUpsertKeybindingResult;
export type ServerRemoveKeybindingResult = typeof ServerRemoveKeybindingResult.Type;

export const ServerConfigUpdatedPayload = Schema.Struct({
  issues: ServerConfigIssues,
  providers: ServerProviders,
  settings: Schema.optional(ServerSettings),
});
export type ServerConfigUpdatedPayload = typeof ServerConfigUpdatedPayload.Type;

export const ServerEnvironmentDisplayNameUpdateInput = Schema.Struct({
  displayName: Schema.NullOr(Schema.String),
});
export type ServerEnvironmentDisplayNameUpdateInput =
  typeof ServerEnvironmentDisplayNameUpdateInput.Type;

export const ServerEnvironmentDisplayNameUpdateResult = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
});
export type ServerEnvironmentDisplayNameUpdateResult =
  typeof ServerEnvironmentDisplayNameUpdateResult.Type;

export const ServerConfigKeybindingsUpdatedPayload = Schema.Struct({
  issues: ServerConfigIssues,
  keybindings: ResolvedKeybindingsConfig,
});
export type ServerConfigKeybindingsUpdatedPayload =
  typeof ServerConfigKeybindingsUpdatedPayload.Type;

export const ServerConfigProviderStatusesPayload = Schema.Struct({
  providers: ServerProviders,
});
export type ServerConfigProviderStatusesPayload = typeof ServerConfigProviderStatusesPayload.Type;

export const ServerConfigSettingsUpdatedPayload = Schema.Struct({
  settings: ServerSettings,
});
export type ServerConfigSettingsUpdatedPayload = typeof ServerConfigSettingsUpdatedPayload.Type;

export const ServerConfigEnvironmentUpdatedPayload = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
});
export type ServerConfigEnvironmentUpdatedPayload =
  typeof ServerConfigEnvironmentUpdatedPayload.Type;

export const ServerConfigStreamSnapshotEvent = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("snapshot"),
  config: ServerConfig,
});
export type ServerConfigStreamSnapshotEvent = typeof ServerConfigStreamSnapshotEvent.Type;

export const ServerConfigStreamKeybindingsUpdatedEvent = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("keybindingsUpdated"),
  payload: ServerConfigKeybindingsUpdatedPayload,
});
export type ServerConfigStreamKeybindingsUpdatedEvent =
  typeof ServerConfigStreamKeybindingsUpdatedEvent.Type;

export const ServerConfigStreamProviderStatusesEvent = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("providerStatuses"),
  payload: ServerConfigProviderStatusesPayload,
});
export type ServerConfigStreamProviderStatusesEvent =
  typeof ServerConfigStreamProviderStatusesEvent.Type;

export const ServerConfigStreamSettingsUpdatedEvent = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("settingsUpdated"),
  payload: ServerConfigSettingsUpdatedPayload,
});
export type ServerConfigStreamSettingsUpdatedEvent =
  typeof ServerConfigStreamSettingsUpdatedEvent.Type;

export const ServerConfigStreamEnvironmentUpdatedEvent = Schema.Struct({
  version: Schema.Literal(1),
  type: Schema.Literal("environmentUpdated"),
  payload: ServerConfigEnvironmentUpdatedPayload,
});
export type ServerConfigStreamEnvironmentUpdatedEvent =
  typeof ServerConfigStreamEnvironmentUpdatedEvent.Type;

export const ServerConfigStreamEvent = Schema.Union([
  ServerConfigStreamSnapshotEvent,
  ServerConfigStreamKeybindingsUpdatedEvent,
  ServerConfigStreamProviderStatusesEvent,
  ServerConfigStreamSettingsUpdatedEvent,
  ServerConfigStreamEnvironmentUpdatedEvent,
]);
export type ServerConfigStreamEvent = typeof ServerConfigStreamEvent.Type;

export const ServerLifecycleReadyPayload = Schema.Struct({
  at: IsoDateTime,
  environment: ExecutionEnvironmentDescriptor,
});
export type ServerLifecycleReadyPayload = typeof ServerLifecycleReadyPayload.Type;

export const ServerLifecycleWelcomePayload = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
  cwd: TrimmedNonEmptyString,
  projectName: TrimmedNonEmptyString,
  bootstrapProjectId: Schema.optional(ProjectId),
  bootstrapThreadId: Schema.optional(ThreadId),
});
export type ServerLifecycleWelcomePayload = typeof ServerLifecycleWelcomePayload.Type;

export const ServerLifecycleStreamWelcomeEvent = Schema.Struct({
  version: Schema.Literal(1),
  sequence: NonNegativeInt,
  type: Schema.Literal("welcome"),
  payload: ServerLifecycleWelcomePayload,
});
export type ServerLifecycleStreamWelcomeEvent = typeof ServerLifecycleStreamWelcomeEvent.Type;

export const ServerLifecycleStreamReadyEvent = Schema.Struct({
  version: Schema.Literal(1),
  sequence: NonNegativeInt,
  type: Schema.Literal("ready"),
  payload: ServerLifecycleReadyPayload,
});
export type ServerLifecycleStreamReadyEvent = typeof ServerLifecycleStreamReadyEvent.Type;

export const ServerLifecycleStreamEvent = Schema.Union([
  ServerLifecycleStreamWelcomeEvent,
  ServerLifecycleStreamReadyEvent,
]);
export type ServerLifecycleStreamEvent = typeof ServerLifecycleStreamEvent.Type;

export const ServerProviderUpdatedPayload = Schema.Struct({
  providers: ServerProviders,
});
export type ServerProviderUpdatedPayload = typeof ServerProviderUpdatedPayload.Type;

// Trace diagnostics — a read over the rotated `server.trace.ndjson` that
// `TraceSink` already writes. Timestamps are ISO strings rather than
// `Schema.DateTimeUtc` because every diagnostics surface in the web app
// formats strings (`DiagnosticsPanelPrimitives.formatTimestamp`), and absent
// values are `optionalKey` rather than `Schema.Option` because these contracts
// have no Option encoding anywhere else.

export const ServerTraceDiagnosticsErrorKind = Schema.Literals([
  "trace-file-not-found",
  "trace-file-read-failed",
]);
export type ServerTraceDiagnosticsErrorKind = typeof ServerTraceDiagnosticsErrorKind.Type;

export const ServerTraceDiagnosticsSpanSummary = Schema.Struct({
  name: TrimmedNonEmptyString,
  count: NonNegativeInt,
  failureCount: NonNegativeInt,
  totalDurationMs: Schema.Number,
  averageDurationMs: Schema.Number,
  maxDurationMs: Schema.Number,
});
export type ServerTraceDiagnosticsSpanSummary = typeof ServerTraceDiagnosticsSpanSummary.Type;

export const ServerTraceDiagnosticsSpanOccurrence = Schema.Struct({
  name: TrimmedNonEmptyString,
  durationMs: Schema.Number,
  endedAt: IsoDateTime,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsSpanOccurrence = typeof ServerTraceDiagnosticsSpanOccurrence.Type;

export const ServerTraceDiagnosticsFailureSummary = Schema.Struct({
  name: TrimmedNonEmptyString,
  cause: TrimmedNonEmptyString,
  count: NonNegativeInt,
  lastSeenAt: IsoDateTime,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsFailureSummary = typeof ServerTraceDiagnosticsFailureSummary.Type;

export const ServerTraceDiagnosticsRecentFailure = Schema.Struct({
  name: TrimmedNonEmptyString,
  cause: TrimmedNonEmptyString,
  durationMs: Schema.Number,
  endedAt: IsoDateTime,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsRecentFailure = typeof ServerTraceDiagnosticsRecentFailure.Type;

export const ServerTraceDiagnosticsLogEvent = Schema.Struct({
  spanName: TrimmedNonEmptyString,
  level: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
  seenAt: IsoDateTime,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsLogEvent = typeof ServerTraceDiagnosticsLogEvent.Type;

export const ServerTraceDiagnosticsError = Schema.Struct({
  kind: ServerTraceDiagnosticsErrorKind,
  message: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsError = typeof ServerTraceDiagnosticsError.Type;

export const ServerTraceDiagnosticsResult = Schema.Struct({
  traceFilePath: TrimmedNonEmptyString,
  scannedFilePaths: Schema.Array(TrimmedNonEmptyString),
  readAt: IsoDateTime,
  recordCount: NonNegativeInt,
  parseErrorCount: NonNegativeInt,
  firstSpanAt: Schema.optionalKey(IsoDateTime),
  lastSpanAt: Schema.optionalKey(IsoDateTime),
  failureCount: NonNegativeInt,
  interruptionCount: NonNegativeInt,
  slowSpanThresholdMs: NonNegativeInt,
  slowSpanCount: NonNegativeInt,
  logLevelCounts: Schema.Record(TrimmedNonEmptyString, NonNegativeInt),
  topSpansByCount: Schema.Array(ServerTraceDiagnosticsSpanSummary),
  slowestSpans: Schema.Array(ServerTraceDiagnosticsSpanOccurrence),
  commonFailures: Schema.Array(ServerTraceDiagnosticsFailureSummary),
  latestFailures: Schema.Array(ServerTraceDiagnosticsRecentFailure),
  latestWarningAndErrorLogs: Schema.Array(ServerTraceDiagnosticsLogEvent),
  // Some rotated files were readable and some were not: the numbers below are
  // real but incomplete, which is a different thing to say than an error.
  partialFailure: Schema.optionalKey(Schema.Boolean),
  error: Schema.optionalKey(ServerTraceDiagnosticsError),
});
export type ServerTraceDiagnosticsResult = typeof ServerTraceDiagnosticsResult.Type;
